/**
 * The Archived artwork section of a Tharstern order's record (One package T3): for each Tharstern job of the order,
 * what the Tharstern artwork archive holds. Read off the record's `archived_artwork` field, which is absent (null)
 * when the archive's feature is off for the person, they lack its permit, or the order names no job. The wording
 * (where a file was filed, none filed, not archived yet) is the backend's; nothing is derived here.
 */
import type { RecordView } from "./object-ui/contract";

export type ArchivedFileStatus = "archived" | "pending" | "failed";
export type ArchivedJobStatus = "listed" | "none_filed" | "not_archived_yet";

export interface ArchivedArtworkFile {
  fileName: string | null;
  /** Where Tharstern said the file was, in words: "Filed under the job's folder, Supplied/". */
  origin: string;
  sizeBytes: number | null;
  status: ArchivedFileStatus;
  /** "Not archived yet." / "Could not be archived."; null when a copy can be downloaded. */
  statusText: string | null;
  /** The archive's own download route (it needs the person's sign-in); null while there is no copy. */
  downloadHref: string | null;
}

export interface ArchivedArtworkJob {
  jobNo: string;
  heading: string;
  status: ArchivedJobStatus;
  /** "None filed under this job, as at 22 Oct 2026." / "Not archived yet."; null when files are listed. */
  statusText: string | null;
  files: ArchivedArtworkFile[];
}

const FILE_STATUSES: readonly string[] = ["archived", "pending", "failed"];
const JOB_STATUSES: readonly string[] = [
  "listed",
  "none_filed",
  "not_archived_yet",
];

function fileOf(row: unknown): ArchivedArtworkFile | null {
  if (typeof row !== "object" || row === null) return null;
  const r = row as Record<string, unknown>;
  if (typeof r["origin"] !== "string") return null;
  if (typeof r["status"] !== "string" || !FILE_STATUSES.includes(r["status"]))
    return null;
  return {
    fileName: typeof r["file_name"] === "string" ? r["file_name"] : null,
    origin: r["origin"],
    sizeBytes: typeof r["size_bytes"] === "number" ? r["size_bytes"] : null,
    status: r["status"] as ArchivedFileStatus,
    statusText: typeof r["status_text"] === "string" ? r["status_text"] : null,
    downloadHref:
      typeof r["download_href"] === "string" && r["download_href"]
        ? r["download_href"]
        : null,
  };
}

/** The jobs of the record's Archived artwork section, or an empty list when the record carries none. */
export function archivedArtworkOf(record: RecordView): ArchivedArtworkJob[] {
  const jobs = record["archived_artwork"];
  if (!Array.isArray(jobs)) return [];
  return jobs.flatMap((row): ArchivedArtworkJob[] => {
    if (typeof row !== "object" || row === null) return [];
    const r = row as Record<string, unknown>;
    if (typeof r["job_no"] !== "string" || typeof r["heading"] !== "string")
      return [];
    if (typeof r["status"] !== "string" || !JOB_STATUSES.includes(r["status"]))
      return [];
    const files = Array.isArray(r["files"]) ? r["files"] : [];
    return [
      {
        jobNo: r["job_no"],
        heading: r["heading"],
        status: r["status"] as ArchivedJobStatus,
        statusText:
          typeof r["status_text"] === "string" ? r["status_text"] : null,
        files: files.flatMap((file) => {
          const parsed = fileOf(file);
          return parsed ? [parsed] : [];
        }),
      },
    ];
  });
}
